import React, { useEffect, useState, useRef, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { MessageCircle, X, Send } from "lucide-react";
import { toast } from "sonner";
import { playNotifySound, showBrowserNotification } from "@/lib/supportNotify";

const AVAIL = {
  online: { dot: "bg-emerald-500", label: "Support Online", cta: "Chat with Support" },
  away: { dot: "bg-amber-500", label: "Support is away", cta: "Message Support" },
  offline: { dot: "bg-slate-400", label: "Message Support", cta: "Message Support" },
};

export function CandidateSupportWidget() {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState("offline");
  const [conv, setConv] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [acked, setAcked] = useState(false);
  const [offlineMsg, setOfflineMsg] = useState("");
  const [replyTime, setReplyTime] = useState("");
  const [invite, setInvite] = useState(null);          // {message} when staff invited
  const [inviteDismissed, setInviteDismissed] = useState(false);
  const [prefs, setPrefs] = useState({ sound: false, browser: false, show_previews: false });
  const endRef = useRef();
  const lastInbound = useRef(0);

  const poll = useCallback(() => {
    api.get("/support/conversation").then((r) => {
      setState(r.data.availability);
      setConv(r.data.conversation);
      setOfflineMsg(r.data.offline_message || "");
      setReplyTime(r.data.reply_time_text || "");
      const c = r.data.conversation;
      if (c?.invitation_pending && c?.invitation_message && !inviteDismissed && !open) setInvite({ message: c.invitation_message });
    }).catch(() => {});
  }, [inviteDismissed, open]);
  useEffect(() => { poll(); const t = setInterval(poll, 15000); return () => clearInterval(t); }, [poll]);
  // Candidate presence heartbeat (presence is NOT CRM activity) + notification prefs
  useEffect(() => {
    api.get("/support/notification-preferences").then((r) => setPrefs(r.data.preferences)).catch(() => {});
    const beat = () => api.post("/support/candidate-heartbeat").catch(() => {});
    beat(); const t = setInterval(beat, 45000); return () => clearInterval(t);
  }, []);

  const loadMsgs = useCallback((cid, notify = false) => {
    if (!cid) return;
    api.get(`/support/conversations/${cid}/messages`).then((r) => {
      const list = r.data.messages || [];
      const inbound = list.filter((m) => !m.is_own && m.sender_type !== "system").length;
      if (notify && inbound > lastInbound.current && lastInbound.current > 0) {
        if (prefs.sound) playNotifySound();
        if (prefs.browser) showBrowserNotification("New reply from Top-Recruit Support",
          prefs.show_previews ? list[list.length - 1]?.message_body : null, () => setOpen(true));
      }
      lastInbound.current = inbound;
      setMsgs(list);
    }).catch(() => {});
    if (open) api.post(`/support/conversations/${cid}/read`).then(poll).catch(() => {});
  }, [poll, open, prefs]);

  const openPanel = async () => {
    setOpen(true); setInvite(null);
    try {
      const { data } = await api.post("/support/conversation", {});
      setConv(data.conversation);
      loadMsgs(data.conversation.id);
    } catch (e) { toast.error(apiError(e)); }
  };
  const acceptInvite = () => openPanel();
  const dismissInvite = () => { setInvite(null); setInviteDismissed(true); api.post("/support/conversation/dismiss-invitation").catch(() => {}); };

  useEffect(() => { const h = () => openPanel(); window.addEventListener("tr-open-support", h); return () => window.removeEventListener("tr-open-support", h); }, []);
  useEffect(() => { if (open && conv?.id) { loadMsgs(conv.id); const t = setInterval(() => loadMsgs(conv.id, true), 8000); return () => clearInterval(t); } }, [open, conv?.id, loadMsgs]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, open]);

  const send = async () => {
    const body = text.trim(); if (!body || !conv) return;
    setText("");
    try {
      await api.post(`/support/conversations/${conv.id}/messages`, { message_body: body });
      loadMsgs(conv.id);
      if (state !== "online" && !acked) setAcked(true);
    } catch (e) { toast.error(apiError(e)); }
  };

  const a = AVAIL[state] || AVAIL.offline;
  const unread = conv?.unread || 0;

  return (
    <>
      {/* Proactive invitation card */}
      {invite && !open && (
        <div data-testid="support-invitation" className="fixed bottom-24 right-5 z-40 w-[300px] rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden">
          <div className="px-4 py-3 bg-[#013A5A] text-white text-sm font-semibold">Top-Recruit Support</div>
          <div className="p-4">
            <p className="text-sm text-slate-700">{invite.message}</p>
            <div className="mt-3 flex gap-2">
              <button onClick={acceptInvite} data-testid="support-invite-accept" className="flex-1 rounded-lg bg-brand text-white text-sm py-2 font-medium hover:bg-brand-strong">Chat with Support</button>
              <button onClick={dismissInvite} data-testid="support-invite-dismiss" className="rounded-lg border border-slate-200 text-slate-500 text-sm py-2 px-3 hover:bg-slate-50">No Thanks</button>
            </div>
          </div>
        </div>
      )}

      {!open && (
        <button onClick={openPanel} data-testid="support-fab"
          className="fixed bottom-20 md:bottom-5 right-5 z-40 flex items-center gap-2.5 rounded-full bg-[#013A5A] text-white pl-3 pr-4 py-3 shadow-lg hover:bg-[#012c44] transition-colors">
          <span className="relative"><MessageCircle className="w-5 h-5" />
            <span className={`absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full ring-2 ring-[#013A5A] ${a.dot}`} /></span>
          <span className="text-sm font-medium">{a.cta}</span>
          {unread > 0 && <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-[10px] font-bold flex items-center justify-center">{unread}</span>}
        </button>
      )}
      {open && (
        <div data-testid="support-panel"
          className="fixed z-50 bg-white shadow-2xl border border-slate-200 flex flex-col
                     inset-0 sm:inset-auto sm:bottom-5 sm:right-5 sm:w-[380px] sm:h-[560px] sm:rounded-2xl overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 bg-[#013A5A] text-white shrink-0">
            <div><p className="font-semibold text-sm">Top-Recruit Support</p>
              <p className="text-[11px] flex items-center gap-1.5 text-white/80"><span className={`w-2 h-2 rounded-full ${a.dot}`} />{a.label}</p></div>
            <button onClick={() => setOpen(false)} data-testid="support-panel-close" className="p-1.5 rounded-lg hover:bg-white/10"><X className="w-4 h-4" /></button>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-slate-50" data-testid="support-messages">
            {msgs.length === 0 && <p className="text-center text-xs text-slate-400 mt-6">Send a message and our team will help you.</p>}
            {msgs.map((m) => (
              m.sender_type === "system" ? (
                <p key={m.id} className="text-center text-[11px] text-slate-400 py-1">{m.message_body}</p>
              ) : (
                <div key={m.id} className={`flex ${m.is_own ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${m.is_own ? "bg-brand text-white rounded-br-sm" : "bg-white border border-slate-200 text-slate-700 rounded-bl-sm"}`}>
                    {!m.is_own && <p className="text-[10px] font-semibold text-slate-400 mb-0.5">{m.sender_name}</p>}
                    <p className="whitespace-pre-wrap break-words">{m.message_body}</p>
                  </div>
                </div>
              )
            ))}
            {state !== "online" && (acked || msgs.some((m) => m.is_own)) && (
              <div className="text-center text-[11px] text-slate-400 py-1" data-testid="support-offline-ack">
                <p>{offlineMsg || "Thanks — your message has been sent to the support team. Support is currently offline; we'll follow up."}</p>
                {replyTime && <p className="mt-0.5">{replyTime}</p>}
              </div>
            )}
            <div ref={endRef} />
          </div>
          <div className="p-3 border-t border-slate-100 flex items-end gap-2 shrink-0">
            <textarea value={text} onChange={(e) => setText(e.target.value)} rows={1} data-testid="support-input"
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Type your message…" className="flex-1 resize-none rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30 max-h-24" />
            <button onClick={send} data-testid="support-send" className="rounded-lg bg-brand text-white p-2.5 hover:bg-brand-strong disabled:opacity-50" disabled={!text.trim()}><Send className="w-4 h-4" /></button>
          </div>
        </div>
      )}
    </>
  );
}
